import { useCallback, useRef } from 'react';
import { Video, PaginatedResponse } from '@repo/types';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { VideoCard } from '../VideoCard/VideoCard';
import { VideoCardSkeleton } from '../VideoCard/VideoCardSkeleton';
import { VideoCarouselProps } from './contracts';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

export type VideoCarouselUIProps = VideoCarouselProps & {
  paginatedVideos: PaginatedResponse<Video>;
  allVideos?: Video[];
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  lazyLoad?: boolean;
};

export default function VideoCarouselUI({
  title,
  paginatedVideos,
  allVideos,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
  lazyLoad = false,
  variant,
  showDate,
  showTag,
  autoplay = false,
  autoplayDelay = 3000,
  loop = false,
  prioritize = false,
}: VideoCarouselUIProps) {
  const swiperRef = useRef<SwiperType | null>(null);

  const videos = lazyLoad && allVideos ? allVideos : paginatedVideos?.items ?? [];

  const handleSlideChange = useCallback(
    (swiper: SwiperType) => {
      if (!lazyLoad || !fetchNextPage || !hasNextPage || isFetchingNextPage) {
        return;
      }

      const slidesPerView =
        typeof swiper.params.slidesPerView === 'number' ? swiper.params.slidesPerView : swiper.slidesPerViewDynamic();
      const currentIndex = swiper.activeIndex;
      const totalSlides = swiper.slides.length;

      // Fetch more when we're within 2 slides of the end
      const threshold = 2;
      if (currentIndex + slidesPerView + threshold >= totalSlides) {
        fetchNextPage();
      }
    },
    [lazyLoad, fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  if (videos.length === 0 && !isFetchingNextPage) {
    return null;
  }

  const autoplayConfig = autoplay
    ? {
        delay: autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      }
    : false;

  const skeletonCount = 3;

  return (
    <div className="article-carousel video-carousel">
      {title && <h3 className="article-carousel__title">{title}</h3>}
      <Swiper
        key={`swiper-autoplay-${autoplay}`}
        modules={[Navigation, Pagination, Autoplay]}
        slidesPerView="auto"
        spaceBetween={16}
        navigation
        pagination={{ clickable: true }}
        autoplay={autoplayConfig}
        loop={lazyLoad ? false : loop}
        className="article-carousel__swiper"
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        onSlideChange={handleSlideChange}
        onReachEnd={() => {
          if (lazyLoad && fetchNextPage && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        }}
      >
        {videos.map((video) => (
          <SwiperSlide key={video.id} className="article-carousel__slide">
            <VideoCard
              variant={variant}
              video={video}
              showDate={showDate}
              showTag={showTag}
              prioritize={prioritize}
            />
          </SwiperSlide>
        ))}
        {lazyLoad &&
          isFetchingNextPage &&
          Array.from({ length: skeletonCount }).map((_, index) => (
            <SwiperSlide key={`skeleton-${index}`} className="article-carousel__slide article-carousel__slide--skeleton">
              <VideoCardSkeleton variant={variant} showTag={showTag} showDate={showDate} />
            </SwiperSlide>
          ))}
      </Swiper>
    </div>
  );
}
